import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import PublicNav from "../../components/PublicNav";
import { CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let count = 0;
    const MAX = 15;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/bookings/status/${sessionId}`);
        setState(r.data);
        if (r.data.status === "paid" || r.data.status === "expired") return;
      } catch {}
      count += 1; setAttempts(count);
      if (count >= MAX) { setState(s => ({ ...s, status: "slow" })); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-40 pb-32">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          {state.status === "paid" ? (
            <>
              <CheckCircle size={64} weight="duotone" className="text-ms-brass mx-auto" />
              <p className="label-eyebrow text-ms-brass mt-6">Confirmed</p>
              <h1 className="serif text-ms-ink text-5xl md:text-6xl mt-4 leading-[0.95]">The table is set.</h1>
              <p className="text-ms-stone mt-5 max-w-lg mx-auto leading-[1.7]">
                A confirmation has been sent to <span className="text-ms-ink">{state.booking?.customer_email}</span>. We can't wait to see you.
              </p>
              <div className="bg-ms-ivory border border-ms-edge rounded-sm p-6 mt-12 text-left max-w-md mx-auto">
                <p className="label-eyebrow text-ms-stone text-[10px]">Booking ID</p>
                <p className="text-ms-ink text-sm mt-1.5 font-mono" data-testid="booking-id">{state.booking?.id}</p>
                <div className="brass-line my-5" />
                <p className="label-eyebrow text-ms-stone text-[10px]">Guests</p>
                <p className="text-ms-ink mt-1.5">{state.booking?.guest_count}</p>
              </div>
              <Link to="/" className="mt-10 inline-block px-7 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs" data-testid="success-home">Return home</Link>
            </>
          ) : state.status === "expired" ? (
            <>
              <h1 className="serif text-ms-ink text-4xl">Session expired</h1>
              <p className="text-ms-stone mt-4">The checkout session expired. Please try again.</p>
              <Link to="/dates" className="mt-6 inline-block px-6 py-3 bg-ms-ink text-ms-cream rounded-full label-eyebrow text-xs">Back to dates</Link>
            </>
          ) : state.status === "slow" ? (
            <>
              <EnvelopeSimple size={48} weight="duotone" className="text-ms-brass mx-auto" />
              <h1 className="serif text-ms-ink text-4xl mt-6">Payment received — confirmation in progress</h1>
              <p className="text-ms-stone mt-4 max-w-lg mx-auto leading-[1.7]" data-testid="success-slow-message">
                Your payment went through, but our confirmation is taking a little longer than usual.
                You'll receive your confirmation email shortly — no need to pay again.
                If it hasn't arrived within 15 minutes, just reply to any email from us or contact info@mysteryserved.com.
              </p>
              <Link to="/" className="mt-8 inline-block px-6 py-3 border border-ms-edge text-ms-ink hover:bg-ms-ink hover:text-ms-cream transition-all rounded-full label-eyebrow text-xs">Return home</Link>
            </>
          ) : (
            <>
              <EnvelopeSimple size={48} weight="duotone" className="text-ms-brass mx-auto" />
              <h1 className="serif text-ms-ink text-4xl mt-6">Confirming your reservation…</h1>
              <p className="text-ms-stone mt-4">Checking with Stripe — attempt {attempts}/15.</p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
